import React, { useState, useEffect } from 'react';
import { ResponseError } from '../models/response-error';
import { ProductBatchListItem } from '../models/product-batch-list-item';
import { AnalysisDatum } from '../models/analysis-datum';
import { CofA } from '../models/c-of-a';
import { CoaComponent } from '../models/coa-component';
import { AttachedImage } from '../models/attached-image';
import { FieldUpdate } from '../models/field-update';
import { CoaRevisionHistory } from '../models/coa-revision-history';
import { ToastProps } from '../models/toast-props';
import { CoaAnalyte } from '../models/coa-analyte';
import { BenchSheetListItem } from '../models/bench-sheet-list-item';
import { FullCoa } from '../models/full-coa';
import { CoaBasicData } from '../models/coa-basic-data';
import { CrudFunctions } from '../utilities/crud-functions';
import AnalysisDataData from './AnalysisDataData';
import CoaGcInformation from './Coa/CoaGcInformation';
import CoaHplcInformation from './Coa/CoaHplcInformation';
import BatchSearch from './BatchSearch';
import CoaConfigurationBuilder from './CoaConfigurationBuilder';
import DmaFooter from './DmaFooter';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { BenchSheetTechnologyType, ModalMessageType } from '../models/enums';

import DataGrid, {
    Column,
    Selection,
    Editing,
    FormItem,
    Toolbar,
    Item as ToolBarItem,
    LoadPanel,
    Paging,
    Button as ColumnButton
} from 'devextreme-react/data-grid';

import DateBox from 'devextreme-react/date-box';
import TextBox from 'devextreme-react/text-box';
import NumberBox from 'devextreme-react/number-box';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import RadioGroup from 'devextreme-react/radio-group';
import Button from 'devextreme-react/button';
import { Template } from 'devextreme-react/core/template';
import Toast from 'devextreme-react/toast';
import List from 'devextreme-react/list';
import DataTemplates from '../templates/data-templates';

//temp
const uncertaintyOptions = [
    { id: 0, name: 'None' },
    { id: 1, name: 'Analytical' },
    { id: 2, name: 'Gravimetric' }
];

let key = 1;

export default function CertificatesOfAnalyisForm() {
    const [coas, setCoas] =
        useState<CofA[]>();

    const [coaComponents, setCoaComponents] =
        useState<CoaComponent[]>([]);

    const [selectedCoaComponent, setSelectedCoaComponent] =
        useState<CoaComponent>();

    const [coaBasicData, setCoaBasicData] =
        useState<CoaBasicData>();

    const [coaAnalytes, setCoaAnalytes] =
        useState<CoaAnalyte[]>();

    const [coa, setCoa] =
        useState<CofA>();

    const [selectedBenchSheetId, setSelectedBenchSheetId] =
       useState<string>();

    const [selectedBatch, setSelectedBatch] =
        useState<ProductBatchListItem>();

    const [batchSearchVisible, setBatchSearchVisible] =
        useState<boolean>();

    const [coaConfigurationBuilderDialogVisible, setCoaConfigurationBuilderDialogVisible] =
        useState<boolean>();

    const [molecularImage, setMolecularImage] =
        useState<AttachedImage>();

    const [coaImage, setCoaImage] =
        useState<AttachedImage>();

    const [coaRevisionHistories, setCoaRevisionHistories] =
        useState<CoaRevisionHistory[]>();

    const [reportServerBaseUrl, setReportServerBaseUrl] =
        useState<string>();

    const [cofATemplate, setCofATemplate] =
        useState<string>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoadErrors, setDataLoadErrors] =
        useState<ResponseError[]>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        if (!dataLoaded)
            getAllData();
    }, []);

    key++;

    const technologyType = coaComponents && coaComponents.length > 0 ? coaComponents[0].technologyId : null;

    let outputFileName = () => {
        let fileName = '';

        //temp
        let versionNumber = coa && coa.versionNumber ? coa.versionNumber : 1;

        if (coaBasicData && coaBasicData.partNumber)
            fileName = `${coaBasicData.partNumber}`;

        if (coaBasicData && coaBasicData.batchName)
            fileName += `-${coaBasicData.batchName}`;

        fileName += `-${versionNumber}`;

        if (coaBasicData && coaBasicData.popNumber)
            fileName += `-${coaBasicData.popNumber}`;

        if (fileName.length > 2)
            return fileName;
        else return "coa-document"
    }

    //let canRunReport = selectedBatch && selectedBatch.batchId && selectedBenchSheetId && outputFileName ? true : false;

    const gcBenchSheetAvailable = coaComponents && coaComponents.find(c => c.technologyId === BenchSheetTechnologyType.GasChromatography ||
        c.technologyId === BenchSheetTechnologyType.HighPerformanceLiquidChromatography);

    return (
        <div className="pw-form-container">
            <div className="pw-container">
                <div className="lq-title-bar pw-form-header">
                    <div className="lq-main-title">Certificates of Analysis</div>
                    <div className="title-bar-spacer" />
                    <div className="save-toolbar pw-comp-5">
                        <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                            hint="go back"
                            height={'2.6em'}
                            onClick={goBack} />
                    </div>
                </div>

                <div>
                    <div className="pw-comp-row">
                        <div className="pw-form-unit">
                            <Button id="toggleBatchSearchButton" text="Search Batches" icon="search" type="success"
                                hint="search batches"
                                height='2.6em'
                                onClick={showBatchSearchDialog} />
                        </div>

                        <div className="pw-form-unit">
                            <div>{selectedBatch ? selectedBatch.catalogNumber : ''}</div>
                        </div>

                        <div className="pw-form-unit">
                            <div>{selectedBatch ? selectedBatch.batchName : ''}</div>
                        </div>

                        <div className="pw-content">
                            <div className="pw-comp-row pw-margin-top-1">
                                <DataGrid id="CoaConfigurationsDataGrid"
                                    height="10rem"
                                    onSelectionChanged={coaSelectionChanged}
                                    onRowValidating={onCoaValidating}
                                    onRowRemoving={onCoaDeleting}
                                    dataSource={coas ? coas : []}
                                    rowAlternationEnabled={true}
                                    showRowLines={true}
                                    showBorders={true}>

                                    <Selection mode='single' />

                                    <Column type="buttons" width='6rem' >
                                        <ColumnButton name="edit" />
                                        <ColumnButton name="delete" />
                                        <ColumnButton hint="audit information" icon="clock"
                                            onClick={showCoasAuditDialog} />
                                        <ColumnButton hint="information" icon="info"
                                            onClick={onCoasInformationButtonClicked} />
                                    </Column>

                                    <Column dataField='name' width='12rem' dataType="string" />
                                    <Column dataField='description' width='30rem' dataType="string" />
                                    <Column dataField='dateReleased' dataType="date" format="MM/dd/yyyy" caption="Date Released" width='7rem' />
                                    <Column dataField='versionNumber' dataType="string" caption="Version #" width='6rem' />
                                    <Column allowEditing={false} />

                                    <Toolbar>
                                        <ToolBarItem location='before'>
                                            <div className="pw-datagrid-toolbar-template">
                                                <div className="pw-datagrid-toobar-title">COA Configurations</div>
                                            </div>
                                        </ToolBarItem>

                                        <ToolBarItem location='after' name="addRowButton"
                                            disabled={!selectedBatch} />

                                        <ToolBarItem location='after'>
                                            <Button type='success' width='2.1rem' icon='refresh'
                                                text='' disabled={!selectedBatch}
                                                hint='generate coa data'
                                                onClick={generateCofAData} />
                                        </ToolBarItem>
                                    </Toolbar>

                                    <Editing
                                        mode="popup"
                                        useIcons={true}
                                        allowAdding={true}
                                        allowUpdating={true}
                                        allowDeleting={true} />

                                </DataGrid>
                            </div>
                        </div>

                        <div className="pw-content">
                            <div className="pw-comp-row pw-margin-top-1">
                                <DataGrid id="coaComponentsDataGrid"
                                    height="10rem"
                                    dataSource={coaComponents ? coaComponents : []}
                                    rowAlternationEnabled={true}
                                    showRowLines={true}
                                    showBorders={true}>

                                    <Selection mode='single' />

                                    {/*
                                <Column type="buttons" width='5rem' >
                                    <ColumnButton hint="delete" icon="trash"
                                        onClick={onStandardRowRemoving} />
                                    <ColumnButton hint="information" icon="info"
                                        onClick={onStandardsInformationButtonClicked} />
                                </Column>
                                */}

                                    <Column dataField='sequence' width='6rem' dataType="number" />
                                    <Column dataField='technologyName' width='10rem' dataType="string" />
                                    <Column />

                                    <Toolbar>
                                        <ToolBarItem location='before'>
                                            <div className="pw-datagrid-toolbar-template">
                                                <div className="pw-datagrid-toobar-title">COA Components</div>
                                            </div>
                                        </ToolBarItem>

                                        <ToolBarItem location='after'>
                                            <Button type='normal' width='2.1rem' icon='edit'
                                                text='' disabled={!coa}
                                                hint='edit coa components'
                                                onClick={createNewConfiguration} />
                                        </ToolBarItem>
                                    </Toolbar>

                                </DataGrid>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="pw-margin-top-1">
                    {coaBasicData && coaBasicData.gravimetricCalculationDescription &&
                        <div className="pw-comp-row">
                            <div className="pw-form-unit pw-comp-20">
                                <div className="pw-form-unit-label">Gravimetric Calculation:</div>
                                <div className="pw-normal pw-margin-left-05 pw-margin-bottom-05">{coaBasicData.gravimetricCalculationDescription}</div>
                            </div>
                            <div className="pw-form-unit pw-margin-right-1">
                                <div className="pw-form-unit-label pw-margin-right-1">Uncertainty Type</div>
                                <div className="pw-margin-bottom-05">
                                    <RadioGroup id="uncertaintyType" items={uncertaintyOptions}
                                        displayExpr={'name'} valueExpr={'id'} layout="horizontal"
                                        onValueChanged={onValueChanged}
                                        value={coa ? coa.uncertaintyType : undefined} />
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>

            {dataLoadErrors && dataLoadErrors.length > 0 &&
                <div className="pw-margin-top-1">
                    <div className="lq-header pw-bg-red">
                        <div className="lq-header-title">Data Load Errors</div>
                    </div>
                    <List items={dataLoadErrors}
                        itemRender={DataTemplates.ResponseErrorTemplate} />
                </div>
            }

            {selectedBatch &&
                <div>
                    <div className="lq-title-bar pw-form-header">
                        <div className="lq-main-title">Certificate of Analysis Values</div>
                    </div>

                    <div className="pw-content">
                        <div className="pw-comp-row">
                            <DataGrid id="coaAnalytesDataGrid"
                                height="20em"
                                wordWrapEnabled={true}
                                dataSource={coaAnalytes ? coaAnalytes : []}
                                rowAlternationEnabled={true}
                                showRowLines={true}
                                showBorders={true}>

                                <LoadPanel enabled={true} />
                                <Selection mode={'single'} />
                                <Paging enabled={false} />

                                <Column dataField={'elution'} width={'8%'} alignment='center' />
                                <Column dataField={'rawMaterialDescription'} caption="Analyte" width={'22%'} />
                                <Column dataField={'casNumber'} caption="CAS #" width={'10%'} />
                                <Column dataField={'assignedValue'} width={'12%'} />
                                <Column dataField={'units'} caption="Units" width={'10%'} />
                                <Column dataField={'purity'} caption="Raw Material Purity, %" width={'12%'} />
                                <Column dataField={'rawMaterialBatchName'} caption="Raw Material Lot" width={'14%'} />

                                <Toolbar>
                                    <ToolBarItem location='before'>
                                        <div className="pw-datagrid-toolbar-template">
                                            <div className="pw-datagrid-toobar-title">Analyte Values</div>
                                        </div>
                                    </ToolBarItem>
                                    <ToolBarItem location='after' options={{ width: '20em' }}
                                        template='bomToolbarBatchInfoTemplate' />
                                </Toolbar>

                                <Template name="bomToolbarBatchInfoTemplate" render={bomToolbarBatchInfoTemplateRender} />

                            </DataGrid>
                        </div>
                    </div>

                    <div className="pw-content">
                        <div className="pw-comp-row">
                            <AnalysisDataData 
                                benchSheetId={selectedBenchSheetId ?
                                    selectedBenchSheetId : undefined}
                                batchId={selectedBatch ? selectedBatch.batchId : undefined}
                                isReadOnly={true}
                                datagridHeight={'16rem'} />
                        </div>
                    </div>

                    {technologyType && technologyType === BenchSheetTechnologyType.GasChromatography &&
                        <CoaGcInformation batchId={selectedBatch.batchId} benchSheetId={selectedBenchSheetId}
                            onError={onGcInformationError} />
                    }

                    {technologyType && technologyType === BenchSheetTechnologyType.HighPerformanceLiquidChromatography &&
                        <CoaHplcInformation batchId={selectedBatch.batchId} benchSheetId={selectedBenchSheetId}
                            onError={onHplcInformationError} />
                    }

                    <div className="pw-content">
                        <div className="lq-header pw-margin-top-05 pw-bg-cyan">
                            <div className="lq-header-title">Images</div>
                        </div>
                    </div>

                    <div className="pw-content">
                        <div className="pw-comp-row">
                            <div className="pw-stacked-components">
                                <div className="textbox-label">Molecular Image</div>
                                {molecularImage &&
                                    <img alt="molecular" className="max-image-width-50"
                                        src={`data:image/png;base64, ${molecularImage ? molecularImage.base64Bytes : ''}`} />
                                }
                                {!molecularImage &&
                                    <div>NA</div>
                                }
                            </div>
                        </div>
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-stacked-components">
                            <div className="textbox-label">C of A Image</div>
                            {coaImage &&
                                <img alt="molecular" className="max-image-width-50"
                                    src={`data:image/png;base64, ${coaImage ? coaImage.base64Bytes : ''}`} />
                            }
                            {!coaImage &&
                                <div>NA</div>
                            }
                        </div>
                    </div>

                    <div className="pw-content">
                        <div className="lq-header pw-margin-top-05 pw-bg-green">
                            <div className="lq-header-title">C of A Generation</div>
                        </div>
                    </div>


                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-15">
                            <label className="textbox-label">Release Date</label>
                            <DateBox id="dateReleased" type="date"
                                displayFormat="MM/dd/yyyy"
                                showClearButton={true}
                                defaultValue={null}
                                onValueChanged={onValueChanged}
                                value={coa && coa.dateReleased ?
                                    coa.dateReleased : undefined}
                                placeholder="release date" />
                        </div>
                        <div className="pw-form-unit pw-comp-15">
                            <label className="textbox-label">Version Number</label>
                            <NumberBox id="versionNumber"
                                showClearButton={true}
                                onValueChanged={onValueChanged}
                                value={coa && coa.versionNumber ?
                                    coa.versionNumber : undefined}
                                placeholder="version number" />
                        </div>
                    </div>

                    <div className="pw-content">
                        <div className="pw-comp-row pw-margin-top-1">
                            <DataGrid
                                height="20em"
                                dataSource={coaRevisionHistories ?
                                    coaRevisionHistories : []}
                                onRowValidating={coaRevisionHistoriesValidating}
                                wordWrapEnabled={true}
                                rowAlternationEnabled={true}
                                showRowLines={true}
                                showBorders={true}>

                                <LoadPanel enabled={true} />
                                <Selection mode={'single'} />

                                <Column dataField={'coaVersion'} caption="Certificate Version" width={'15em'} alignment='center' />
                                <Column dataField={'versionNumber'} caption="Version #" width={'10em'} alignment='center' />
                                <Column dataField={'coaDate'} dataType="date" width={'15em'} />
                                <Column dataField={'reason'} caption="Reason for Version" width={'30em'}>
                                    <FormItem colSpan={2} />
                                </Column>

                                <Toolbar>
                                    <ToolBarItem location='before'>
                                        <div className="pw-datagrid-toolbar-template">
                                            <div className="pw-datagrid-toobar-title">COA Revision History</div>
                                        </div>
                                    </ToolBarItem>
                                    <ToolBarItem name="addRowButton" location='after' />
                                </Toolbar>

                                <Editing
                                    mode="popup"
                                    useIcons={true}
                                    allowAdding={true}
                                    allowUpdating={true}
                                    allowDeleting={true} >
                                </Editing>

                            </DataGrid>
                        </div>
                    </div>

                    {selectedBatch && gcBenchSheetAvailable &&
                        <div className='pw-comp-row pw-margin-top-1'>
                            <Button type='success' text='Run Report' icon="pdffile"
                                height='2.6em'
                                onClick={onRunReportClick} />
                        </div>
                    }

                    {/*
                    <div className="pw-comp-row pw-margin-top-1">
                        {canRunReport &&
                            <div className="pw-form-unit pw-comp-20 pw-vertical-center pw-margin-bottom-1">
                                <a href={selectedBatch && selectedGcBenchSheetId ?
                                    `${reportServerBaseUrl}/coa/${selectedBatch.batchId}/${selectedGcBenchSheetId}/${outputFileName()}/true` : ''}
                                    rel="noopener noreferrer" >
                                    Download Report
                                </a>
                            </div>
                        }
                       
                        {!canRunReport &&
                            <div className="pw-form-unit pw-vertical-center pw-margin-bottom-1">
                                <div className="textbox-label">Select a batch and benchsheet in order to run a report</div>
                            </div>
                        }
                    </div>
                    */}

                </div>
            }

            {batchSearchVisible &&
                <DialogPopup key={`ciSearch${key}`}
                    visible={batchSearchVisible}
                    onHiding={hideBatchSearchDialog}
                    hideOnOutsideClick={true}
                    dragEnabled={true}
                    resizeEnabled={false}
                    title="Batches Search"
                    width='45rem'
                    height='40rem'>
                    <BatchSearch
                        onCancel={hideBatchSearchDialog}
                        onSubmit={applySelectedBatch} />
                </DialogPopup>
            }

            {coaConfigurationBuilderDialogVisible &&
                <DialogPopup key={`ccb${key}`}
                    visible={coaConfigurationBuilderDialogVisible}
                    onHiding={hideCoaConfigurationBuilderDialog}
                    title="COA Configuration Builder"
                    fullScreen={true} >
                    <CoaConfigurationBuilder coaId={coa ? coa.coaId : undefined}
                        selectedBatch={selectedBatch ? selectedBatch : undefined}
                        onClose={hideCoaConfigurationBuilderDialog} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

            <DmaFooter />
        </div>
    );

    function applySelectedBatch(batch: ProductBatchListItem) {
        if (batch.batchId)
            getCoasByBatchId(batch.batchId);

        setSelectedBatch(batch);
        hideBatchSearchDialog();
    }

    //change events

    function onValueChanged(e: any) {
        if (!dataLoaded || !selectedBatch || !coa)
            return;

        const caller = e.element.id as string;

        if (caller) {
            const currentValue = coa[caller];

            if (currentValue === e.value)
                return;
            else if (!currentValue && !e.value)
                return;
            else {
                updateField("Coas", caller, e.value)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Field");
                    });
            }

            setCoa({ ...coa, [caller]: e.value });
        }
    }

    function coaSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            setCoa(e.selectedRowsData[0]);

            if (e.selectedRowsData[0].coaId)
                getCoaComponents(e.selectedRowsData[0].coaId);
        }
    }

    async function onCoaValidating(e: any) {
        if (!selectedBatch)
            return;

        if (e.oldData) {
            let coa = e.oldData as CofA;

            if (coa) {
                let updatedCoa = { ...coa, ...e.newData };

                await updateCoa(updatedCoa)
                    .then(() => {
                        showToast("COA updated", "success");
                    })
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating COA");
                    });
            }
        }
        else {
            if (e.newData) {
                let coa = e.newData as CofA;

                if (coa) {
                    coa.batchId = selectedBatch.batchId;

                    await insertCoa(coa)
                        .then(data => {
                            showToast("COA saved", "success");

                            if (selectedBatch && selectedBatch.batchId)
                                getCoasByBatchId(selectedBatch.batchId);
                        })
                        .catch(res => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting COA");
                        });
                }
            }
        }
    }

    async function onCoaDeleting(e: any) {
        if (coa && coa.coaId) {
            await deleteCoa(coa.coaId)
                .then(() => {
                    showToast("COA deleted", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting COA");
                });
        }
    }

    function createNewConfiguration() {
        showCoaConfigurationBuilderDialog();
    }

    //validating

    function coaRevisionHistoriesValidating(e: any) {
        //update

        if (coa && coa.coaId) {
            if (e.oldData) {
                let coaRevisionHistory = e.oldData as CoaRevisionHistory;

                if (coaRevisionHistory) {
                    let updatedCoaRevisionHistory = { ...coaRevisionHistory, ...e.newData };

                    updateCoaRevisionHistory(updatedCoaRevisionHistory);
                }
            }
            //insert
            else {
                let coaRevisionHistory = e.newData as CoaRevisionHistory;
                coaRevisionHistory.coaId = coa.coaId

                if (coaRevisionHistory) {
                    insertCoaRevisionHistory(coaRevisionHistory);
                }
            }
        }
    }

    function onGcInformationError(error: ResponseError) {
        let currentErrors = [...dataLoadErrors];

        if (!currentErrors) {
            currentErrors = [];
        }

        currentErrors.push(error);
    }

    function onHplcInformationError(error: ResponseError) {
        let currentErrors = [...dataLoadErrors];

        if (!currentErrors) {
            currentErrors = [];
        }

        currentErrors.push(error);
    }

    async function generateCofAData() {
        if (selectedBatch && selectedBatch.batchId && coaComponents && coaComponents.length > 0) {
            let benchSheetId = coaComponents[0].benchSheetId;

            if (!benchSheetId)
                return;

            setDataLoadErrors(null);

            const technologyId = coaComponents[0].technologyId;
            const batchId = selectedBatch.batchId;

            await getCoaAnalytes(batchId, benchSheetId);
            await getCoaBasicData(batchId, technologyId);
            await getMolecularImage(batchId);
            await getCoaImage(batchId);
            await getCoaRevisionHistories(batchId);

            //console.log("DONE GETTING DATA...");

                    if (dataLoadErrors && dataLoadErrors.length > 0) {
                        console.log("Some errors happened");
                    }
        }
    }

    async function addDataLoadError(res: any) {
        try {
            let error: ResponseError = { message: '' };

            if (res.message)
                error.message = res.message;
            else
                error = await res.json() as ResponseError;

            let currentErrors = dataLoadErrors ? [...dataLoadErrors] : [];

        if (!currentErrors)
            currentErrors = [];

        currentErrors.push(error);

        setDataLoadErrors(currentErrors);

        } catch (e) {
            //ignore
        }
    }

    async function onRunReportClick() {
        if (selectedBatch && coaComponents) {
            if (coaComponents.length > 1) {
                const title = "Too Many Bench Sheets Components";
                const message = "You must define only one bench sheet component, and it must be either GC or HPLC";

                showInformationMessage(ModalMessageType.Information, title, message);
                return;
            }

            if (coaComponents[0].technologyId !== BenchSheetTechnologyType.GasChromatography &&
                coaComponents[0].technologyId !== BenchSheetTechnologyType.HighPerformanceLiquidChromatography) {
                const title = "Technology Not Implemented";
                const message = "The current acceptable bench sheet technologies are either GC or HPLC";

                showInformationMessage(ModalMessageType.Information, title, message);
                return;
            }

            await CrudFunctions.getDataBlob(`coatools/generatereport/${selectedBatch.batchId}`)
                .then((blob: any) => {
                    let a = document.createElement("a") as HTMLAnchorElement;

                    document.body.appendChild(a);
                    a.style.display = "none";

                    var url = window.URL.createObjectURL(blob);
                    a.href = url;
                    //todo:
                    a.download = `COA-${selectedBatch.batchName}.pdf`;
                    a.click();
                    window.URL.revokeObjectURL(url);

                    //to open PDF in browser:
                    // let file = window.URL.createObjectURL(blob);
                    // window.open(file, "_blank");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving COAs");
                });
        }
    }

    async function getCoasByBatchId(batchId: string) {
        await CrudFunctions.getData(`coa/coasbybatchid/${batchId}`)
            .then((data) => {
                setCoas(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving COAs");
            });
    }

    async function getCoaComponents(coaId: string) {
        await CrudFunctions.getData(`coa/coacomponentsbycoaid/${coaId}`)
            .then((data) => {
                let components = data as CoaComponent[];

                if (components && components.length > 0) {
                    setSelectedBenchSheetId(components[0].benchSheetId);
                }

                setCoaComponents(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving COA Components");
            });
    }

    async function getCoaRevisionHistories(batchId: string) {
        //console.log("GETTING getCoaRevisionHistories()")

        await CrudFunctions.getData(`coa/coarevisionhistory/${batchId}`)
            .then((data) => {
                setCoaRevisionHistories(data);
            })
            .catch((res) => {
                addDataLoadError(res);
            });
    }

    async function getCoaAnalytes(batchId: string, benchSheetId: string) {
        //console.log("GETTING getCoaAnalytes()")

        await CrudFunctions.getData(`coa/analytedata/${batchId}/${benchSheetId}`)
            .then(data => {
                setCoaAnalytes(data);
            })
            .catch((res) => {
                addDataLoadError(res);
            });
    }

    async function getCoaBasicData(batchId: string, technologyId: BenchSheetTechnologyType) {
        //console.log("GETTING getCoaBasicData()")

        await CrudFunctions.getData(`coa/coabasicdata/${batchId}/${technologyId}`)
            .then(data => {
                setCoaBasicData(data);
            })
            .catch((res) => {
                addDataLoadError(res);
            });
    }

    async function getMolecularImage(batchId: string) {
        //console.log("GETTING getMolecularImage()")

        await CrudFunctions.getData(`coa/molecularimage/${batchId}`)
            .then((data) => {
                setMolecularImage(data);
            })
            .catch(async (res) => {
                addDataLoadError(res);
            });
    }

    async function getCoaImage(batchId: string) {
        //console.log("GETTING getCoaImage()")

        await CrudFunctions.getData(`coa/image/${batchId}`)
            .then((data) => {
                setCoaImage(data);
            })
            .catch((res) => {
                addDataLoadError(res);
            });
    }

    async function getReportServerBaseUrl() {
        await CrudFunctions.getData("common/reportserver")
            .then(data => {
                if (data && data.length === 2) {
                    setReportServerBaseUrl(data[0]);
                    setCofATemplate(data[1]);
                }
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Report Server URL");
            });
    }

    //POST functions

    async function insertCoa(coa: CofA) {
        return await CrudFunctions.postData("coa/coa", coa);
    }

    // PUT functions

    async function updateCoa(coa: CofA) {
        return await CrudFunctions.putData("coa/coa", coa);
    }

    async function updateField(entity: string, fieldName: string, value: any) {
        if (!dataLoaded)
            return;

        if (coa && coa.coaId) {
            let fieldUpdate: FieldUpdate = {
                entity: entity,
                field: fieldName,
                value: value,
                rowId: coa.coaId,
            };

            return await CrudFunctions.putData("calculations/coa", fieldUpdate);
        }
    }

    async function insertCoaRevisionHistory(coaRevisionHistory: CoaRevisionHistory) {
        return await CrudFunctions.postData("coa/coarevisionhistory", coaRevisionHistory);
    }

    async function updateCoaRevisionHistory(coaRevisionHistory: CoaRevisionHistory) {
        return await CrudFunctions.putData("coa/coarevisionhistory", coaRevisionHistory);
    }

    // DELETE functions

    async function deleteCoa(coaId: string) {
        return await CrudFunctions.deleteData(`coa/coa/${coaId}`);
    }

    function showCoasAuditDialog(e: any) {

    }

    function onCoasInformationButtonClicked(e: any) {

    }

    //render

    function bomToolbarBatchInfoTemplateRender(e: any) {
        return (
            <div className="pw-form-unit">
                <label className="pw-form-unit-label" style={{ color: '#fff' }}>Batch</label>
                <div className="pw-form-unit-label" style={{ color: '#fff' }}>{selectedBatch ? selectedBatch.batchName : ''}</div>
            </div>
        );
    }

    //show/hide

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function showBatchSearchDialog() {
        setBatchSearchVisible(true);
    }

    function hideBatchSearchDialog() {
        setBatchSearchVisible(false);
    }

    function showCoaConfigurationBuilderDialog() {
        setCoaConfigurationBuilderDialogVisible(true);
    }

    function hideCoaConfigurationBuilderDialog() {
        setCoaConfigurationBuilderDialogVisible(false);

        if (coa)
            getCoaComponents(coa.coaId);
    }

    //navigation

    function goBack() {
        history.back();
    }

    async function getAllData() {
        await getReportServerBaseUrl()
            .then(() => {
                setDataLoaded(true);
            });
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function showInformationMessage(mode: ModalMessageType, title: string, message: string) {
        let mm: ModalMetadata = {
            error: null,
            mode: mode,
            title: title,
            message: message
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }


}
